import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '@/api/http-client';
import { authControllerRegistrar } from '@/api/generated/auth/auth';
import { AuthShell } from '@/features/auth/components/AuthShell';
import { UFS } from '@/features/auth/schemas/login.schema';
import { ROTAS } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { InputSenha } from '@/shared/ui/input-senha';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

const registroSchema = z
  .object({
    nomeCompleto: z.string().min(3, 'Informe o nome completo'),
    email: z.email('Informe um e-mail válido'),
    crmv: z.string().min(1, 'Informe o CRMV'),
    ufCrmv: z.enum(UFS, 'Selecione a UF'),
    senha: z.string().min(8, 'A senha precisa de ao menos 8 caracteres'),
    confirmarSenha: z.string(),
    // Sem valor "não marcado" válido — recusar cadastro sem aceite é o
    // ponto inteiro do campo. `.refine` em vez de `z.literal(true)` porque
    // este é quem entrega a mensagem de erro certa ao lado do checkbox.
    aceitaTermos: z
      .boolean()
      .refine((v) => v, 'É necessário aceitar os Termos de Uso e a Política de Privacidade'),
  })
  .refine((d) => d.senha === d.confirmarSenha, {
    message: 'As senhas não conferem',
    path: ['confirmarSenha'],
  });

type RegistroForm = z.infer<typeof registroSchema>;

const CAMPO = 'bg-surface-raised border-brand/30 focus:border-brand focus:ring-brand/20 placeholder:text-muted-foreground';

/**
 * O cadastro cria user + profile + tenant INDIVIDUAL + cargo Médico numa
 * transação só (B3-3). Falhando qualquer passo, nada fica no banco.
 */
export function RegistroPage() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegistroForm>({
    resolver: zodResolver(registroSchema),
    defaultValues: { aceitaTermos: false },
  });

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      await authControllerRegistrar({
        nomeCompleto: dados.nomeCompleto,
        email: dados.email,
        senha: dados.senha,
        crmv: dados.crmv,
        ufCrmv: dados.ufCrmv,
      });
      // Não loga direto: em produção `requireEmailVerification` recusaria
      // esse sign-in mesmo (better-auth.ts) — o e-mail precisa ser
      // confirmado primeiro. `VerificarEmailPage` já cria a sessão sozinha
      // quando o código bate.
      navigate(ROTAS.verificarEmail, { state: { email: dados.email } });
    } catch (erro) {
      setError('root', {
        message:
          erro instanceof ApiError ? erro.message : 'Não foi possível criar a conta. Tente de novo.',
      });
    }
  });

  return (
    <AuthShell descricao="Crie sua conta" larguraMaxima="max-w-lg">
      <form onSubmit={aoEnviar} className="space-y-4" noValidate>
        {errors.root?.message && (
          <Alert variant="destructive" className="bg-red-100 border-red-300 text-red-800">
            <AlertDescription>{errors.root.message}</AlertDescription>
          </Alert>
        )}

        <Campo id="nomeCompleto" rotulo="Nome completo" erro={errors.nomeCompleto?.message}>
          <Input id="nomeCompleto" className={CAMPO} placeholder="Seu nome" {...register('nomeCompleto')} />
        </Campo>

        <Campo id="email" rotulo="Email" erro={errors.email?.message}>
          <Input id="email" type="email" className={CAMPO} placeholder="voce@exemplo.com" autoComplete="username" {...register('email')} />
        </Campo>

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Campo id="crmv" rotulo="CRMV" erro={errors.crmv?.message}>
              <Input id="crmv" className={CAMPO} placeholder="Número do CRMV" {...register('crmv')} />
            </Campo>
          </div>
          <Campo id="ufCrmv" rotulo="UF" erro={errors.ufCrmv?.message}>
            <Select value={watch('ufCrmv')} onValueChange={(v) => setValue('ufCrmv', v as RegistroForm['ufCrmv'])}>
              <SelectTrigger id="ufCrmv" className={`${CAMPO} text-brand`}>
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent className="bg-surface-raised border-brand/30">
                {UFS.map((uf) => (
                  <SelectItem key={uf} value={uf} className="text-brand focus:bg-brand/10">
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>

        <Campo id="senha" rotulo="Senha" erro={errors.senha?.message}>
          <InputSenha id="senha" className={CAMPO} placeholder="Mínimo 8 caracteres" autoComplete="new-password" {...register('senha')} />
        </Campo>

        <Campo id="confirmarSenha" rotulo="Confirmar senha" erro={errors.confirmarSenha?.message}>
          <InputSenha id="confirmarSenha" className={CAMPO} placeholder="Repita a senha" autoComplete="new-password" {...register('confirmarSenha')} />
        </Campo>

        <div className="space-y-1.5">
          <div className="flex items-start gap-2">
            <Checkbox
              id="aceitaTermos"
              className="mt-0.5"
              checked={watch('aceitaTermos')}
              onCheckedChange={(marcado) => setValue('aceitaTermos', marcado === true, { shouldValidate: true })}
            />
            <Label htmlFor="aceitaTermos" className="font-normal leading-snug cursor-pointer text-foreground">
              Li e aceito os{' '}
              <Link
                to={ROTAS.termosDeUso}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:text-brand/80 font-semibold hover:underline"
              >
                Termos de Uso
              </Link>{' '}
              e a{' '}
              <Link
                to={ROTAS.politicaDePrivacidade}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:text-brand/80 font-semibold hover:underline"
              >
                Política de Privacidade
              </Link>
            </Label>
          </div>
          {errors.aceitaTermos?.message && (
            <p className="text-xs text-destructive">{errors.aceitaTermos.message}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-brand hover:bg-brand/80 text-white shadow-lg hover:shadow-xl transition-all"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Criando conta...
            </>
          ) : (
            'Criar conta'
          )}
        </Button>

        <div className="text-center text-sm text-muted-foreground mt-4">
          Já tem uma conta?{' '}
          <Link to={ROTAS.login} className="text-brand hover:text-brand/80 font-semibold hover:underline">
            Entrar
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

function Campo({
  id,
  rotulo,
  erro,
  children,
}: {
  id: string;
  rotulo: string;
  erro?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-brand font-medium">
        {rotulo}
      </Label>
      {children}
      {erro && <p className="text-xs text-destructive">{erro}</p>}
    </div>
  );
}
